import { IconFont } from '@/components';
import {
  getBuiltinChars,
  getBuiltinCharsConfig,
  updateBuiltinChars,
} from '@/services/compare/compareSettings';
import { createSharedState } from '@ai-indeed/hooks';
import { Alert, Button, Checkbox } from '@ai-indeed/idp-ui';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { App, Modal, Tooltip } from 'antd';
import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useCharacterSet } from '../IgnoreCustomCharacter';
import './index.less';

interface PunctuationRecord {
  value: string;
  checked: boolean;
}

export const useCheckedList = createSharedState<PunctuationRecord[]>([]);
const CheckCubeBox = (props: {
  record: PunctuationRecord;
  checkAll: boolean;
}) => {
  const { record, checkAll } = props;
  const { value, checked } = record;
  const [checkedList, setCheckedList] = useCheckedList();
  return (
    <div
      className={classNames(
        'w-9 h-9 flex justify-center items-center cursor-pointer rounded-[4px] border-solid border-[#E5E7EC] border-[1px]',
        checked && 'bg-[#EDF0FE] text-primary',
        checkAll && 'bg-[#EDF0FE]',
      )}
      onClick={() => {
        setCheckedList(
          checkedList.map((record) => {
            if (record.value === value)
              return {
                value: value,
                checked: checked ? false : true,
              };
            return record;
          }),
        );
      }}
    >
      {value}
    </div>
  );
};

export default NiceModal.create(() => {
  const { useApp } = App;
  const { message } = useApp();
  const { remove } = useModal();
  // 标点符号
  const [checkedList, setCheckedList] = useCheckedList();
  // 自定义字符
  const [characterSet] = useCharacterSet();
  const [checkedCharacterSetLen, setCheckedCharacterSetLen] = useState(0);
  // 基础字符库
  const baseChars = useMemo(() => getBuiltinChars(), []);
  // checkebox 状态设置
  const checkAll = checkedList.every((record) => record.checked);
  const indeterminate =
    checkedCharacterSetLen > 0 && checkedCharacterSetLen < checkedList.length;
  const onCheckAllChange = (checked: boolean) =>
    setCheckedList(
      checkedList.map((record) => {
        return {
          value: record.value,
          checked: checked,
        };
      }),
    );
  useEffect(() => {
    setCheckedCharacterSetLen(
      checkedList.reduce((pre, cur) => {
        return pre + (cur.checked ? 1 : 0);
      }, 0),
    );
  }, [checkedList]);
  useEffect(() => {
    baseChars.then((resRule) => {
      if (checkedList.length > 0) return;
      getBuiltinCharsConfig().then((res) => {
        const builtinChars = res.data.regulation.char_filter_out.builtin;
        if (builtinChars.length === 0) {
          setCheckedList(
            resRule.data.map((char) => {
              return {
                value: char,
                checked: true,
              };
            }),
          );
        } else {
          setCheckedList(
            resRule.data.map((char) => {
              return {
                value: char,
                checked: builtinChars.find((value) => value === char)
                  ? true
                  : false,
              };
            }),
          );
        }
      });
    });
  }, []);
  return (
    <Modal
      width={968}
      className="modal-reset"
      centered
      open={true}
      onCancel={() => {
        remove();
        setCheckedList([]);
      }}
      title={
        <div className="flex gap-1 justify-center">
          <div>忽略标点符号</div>
          <Tooltip title="选中的标点符号，将不作为比对中的差异点">
            <IconFont type="icon-bangzhu2" className="text-icon-3"></IconFont>
          </Tooltip>
        </div>
      }
      footer={
        <>
          <Button
            type="outline1"
            size="large"
            className="w-[86px]"
            onClick={() => {
              remove();
              setCheckedList([]);
            }}
          >
            取消
          </Button>
          <Button
            type="primary"
            size="large"
            className="w-[86px]"
            onClick={() => {
              updateBuiltinChars({
                char_filter_out: {
                  builtin: checkedList
                    .filter((record) => record.checked)
                    .map((res) => res.value),
                  customized: characterSet
                    .filter((record) => record.checked)
                    .map((res) => res.value),
                },
                filter_type: 'BUILTIN',
              }).then(() => {
                message.success('保存成功');
                remove();
              });
            }}
          >
            确定
          </Button>
        </>
      }
    >
      <div className="relative w-full max-h-[578px] overflow-auto ">
        <Alert
          message="本次修改将应用于当前及后续任务中"
          className="sticky top-0 rounded-none"
          type="info"
          closable
          showIcon
        />
        <section className="ml-6 mr-[14px] my-4">
          <div className="flex justify-between items-center">
            <h3>标点符号</h3>
            <Checkbox
              checked={checkAll}
              indeterminate={indeterminate}
              onChange={(e) => onCheckAllChange(e.target.checked)}
            >
              全选
            </Checkbox>
          </div>
        </section>
        <section className="cubeWrap">
          {checkedList?.map((record, index) => (
            <CheckCubeBox
              key={'char' + index}
              record={record}
              checkAll={checkAll}
            />
          ))}
        </section>
      </div>
    </Modal>
  );
});
